import Text from '../../../../elements/Typo/Text'
import React, { useEffect, useRef, useState } from 'react'
import { BarChart, Bar, Rectangle, ReferenceArea, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Text as RechartsText, LabelList } from 'recharts'
import { formatNumberWithCommas } from '../../../../_common/functions'
import Button from '../../../../elements/button'
import Icon from '../../../../components/icons'
import { useExperimentSelector } from '../../../../store/selectors/experimentSelector'
import { getChartColor } from '../../../../_config'
import { useTranslation } from 'react-i18next'

interface Props {
    title: string
    data: any[]
    className?: string
}

const CustomizedLabel = (props: any) => {
    const { x, y, stroke, value, viewBox } = props

    // Adjust x and y to position the label above the bar and center it horizontally
    const adjustedX = x + viewBox.width / 2 + 4
    const adjustedY = y - 25 // Adjust this value to control the distance from the bar

    return (
        <RechartsText
            x={adjustedX}
            y={adjustedY}
            stroke={stroke}
            fontSize={10}
            fontWeight={500}
            textAnchor="middle" // Center the text horizontally
            verticalAnchor="end" // Align the bottom of the text with adjustedY
            transform={`rotate(-90 ${adjustedX}, ${adjustedY})`} // Rotate the text vertically
            fill="#000" // Set your desired text color
        >
            {`${value}%`}
        </RechartsText>
    )
}

const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload?.length) {
        return (
            <div className="bg-white shadow-lg rounded-[0.667rem] p-3">
                {payload[0].payload.groups.map((group: any, i: any) => (
                    <div className="flex items-center gap-2" key={`tooltip-${group.name}-${i}`}>
                        <div className="w-[0.583rem] h-[0.583rem] rounded-[0.167rem]" style={{ background: getChartColor(group.is_control ? 'control' : 'test', i) }}></div>
                        <Text size="sm" className="flex-grow text-[#74788D] mr-4">
                            {group.name}
                        </Text>
                        <Text size="sm" className="font-semibold">
                            {formatNumberWithCommas(group.count)}
                        </Text>
                    </div>
                ))}
            </div>
        )
    }

    return null
}

const CustomTick = (props: any) => {
    const { x, y, payload } = props

    const maxLength = 12 // Maximum characters to show
    const originalText = payload.value
    const truncatedText = originalText.length > maxLength ? originalText.substring(0, maxLength) + '...' : originalText

    return (
        <g transform={`translate(${x},${y})`}>
            <text x={0} y={0} dy={16} textAnchor="middle" fill="#666" transform="rotate(0)" fontSize="10" style={{ cursor: 'pointer' }}>
                {truncatedText}
                {<title>{originalText}</title>}
            </text>
        </g>
    )
}

const GraphData: React.FC<Props> = ({ title, data, className = '' }) => {
    const { t } = useTranslation()
    const scrollContainerRef = useRef<HTMLDivElement | null>(null)

    const [ticks, setTicks] = useState<any>([10, 20, 30, 40, 50])
    const chartWidth = 0
    const [zoomArea, setZoomArea] = useState<{
        data: any[]
        refAreaLeft: string
        refAreaRight: string
        top: number
        left: number
        right: number
        bottom: number
    }>({
        data,
        refAreaLeft: '',
        refAreaRight: '',
        top: 0,
        bottom: 0,
        left: 0,
        right: data.length,
    })

    const getDefaultBarWidth = (rem: number) => {
        const rootFontSize = Number.parseFloat(getComputedStyle(document.documentElement).fontSize)
        return rem * rootFontSize
    }

    const barGapWidth = 8 // Gap between bars in the same group
    const barWidth = getDefaultBarWidth(1.25) // Width in pixels for each individual bar
    const experiment: any = useExperimentSelector()

    useEffect(() => {
        if (data) {
            const totalPercentages: any = data.reduce((a, c) => {
                c.groups.map((gr: any) => {
                    if (a[gr.name]) {
                        a[gr.name] = +(a[gr.name] + (gr.value ?? 0)).toFixed(2)
                    } else {
                        a[gr.name] = +(gr.value ?? 0).toFixed(2)
                    }
                })
                return a
            }, {})

            Object.keys(totalPercentages).forEach((key) => {
                const percentage = totalPercentages[key]
                if (percentage !== 100) {
                    data.at(-1)?.groups.forEach((group: any) => {
                        if (group.name === key) {
                            group.value = Number((100 - percentage + group.value).toFixed(2))
                        }
                    })
                }
            })

            let highestValue = 0
            for (const columnData of data) {
                columnData.groups.map((group: any) => {
                    if (highestValue < group.value) {
                        highestValue = group.value
                    }
                })
            }

            const _ticks = [0]
            for (let i = 1; i < Math.ceil(highestValue / 10); i++) {
                _ticks.push(i * 10)
            }

            if (highestValue < 10) {
                const num = Math.ceil(highestValue)
                const arr = Array.from({ length: num + 1 }, (_, i) => i)
                console.log(arr)
                setTicks(arr)
            } else {
                setTicks(_ticks)
            }

            console.log('Ticks:', highestValue, _ticks)

            console.log('Data processed:', data)
        }
    }, [data])

    const selectZoneAndZoomIn = () => {
        if (data) {
            const { left, right } = zoomArea
            const middle = Math.floor((left + right) / 2)

            const currentRange = right - left

            // Zoom in by 20% from each side (40% total zoom)
            const zoomPercentage = 0.2
            const shiftingBy = Math.max(1, Math.floor(currentRange * zoomPercentage))

            const updatedLeft = Math.min(middle, left + shiftingBy)
            const updatedRight = Math.max(middle, right - shiftingBy)
            const selectedData = data.slice(updatedLeft, updatedRight)

            if (selectedData.length) {
                setZoomArea({
                    ...zoomArea,
                    data: selectedData,
                    refAreaLeft: '',
                    refAreaRight: '',
                    left: updatedLeft,
                    right: updatedRight,
                })
            }
        }
    }

    const handleMouseUp = () => {
        if (data) {
            let { refAreaLeft, refAreaRight } = zoomArea
            if (/*refAreaLeft === refAreaRight ||*/ refAreaRight === '') {
                setZoomArea((prevState: any) => ({
                    ...prevState,
                    refAreaLeft: '',
                    refAreaRight: '',
                }))
                return
            }
            const indx = data?.findIndex((i) => i.name === refAreaLeft)
            const indx2 = data?.findIndex((i) => i.name === refAreaRight)

            if (indx > indx2) [refAreaLeft, refAreaRight] = [refAreaRight, refAreaLeft]
            setZoomArea((prevState: any) => ({
                ...prevState,
                data: prevState.data.slice(indx, indx2 + 1),
                refAreaLeft: '',
                refAreaRight: '',
                left: indx,
                right: indx2,
            }))
        }
    }

    const zoomOut = () => {
        setZoomArea({
            data,
            refAreaLeft: '',
            refAreaRight: '',
            top: 0,
            bottom: 0,
            left: 0,
            right: data.length,
        })
    }

    const handleMouseDown = (e: any) => {
        if (e?.activeLabel) {
            setZoomArea((prevState: any) => ({
                ...prevState,
                refAreaLeft: e.activeLabel,
            }))
        }
    }
    const graphPageCount: number = 13
    const [graphPagination, setGraphPagination] = useState<number>(0)

    const onBarChartMouseMove = (e: any) => {
        if (zoomArea.refAreaLeft) {
            setZoomArea((prevState: any) => ({
                ...prevState,
                refAreaRight: e?.activeLabel || '',
            }))
        }
    }

    const paginationPrev = () => {
        setGraphPagination((prev) => {
            if (prev > 0) {
                return prev - 1
            }
            return prev
        })
    }
    const paginationNext = () => {
        setGraphPagination((prev) => {
            if (prev < Math.ceil(data.length / graphPageCount) - 1) {
                return prev + 1
            }
            return prev
        })
    }

    return (
        <>
            {data?.length ? (
                <div className={`border border-[#0000001A] rounded-[0.625rem] p-2 overflow-x-auto ${className}`} ref={scrollContainerRef}>
                    <div className="flex items-center justify-between mb-2">
                        <Text size="lg" className="font-semibold">
                            {t('Test / Control Group Allocation by')} {title}
                        </Text>
                        <div className="">
                            <div className="flex flex-col items-center gap-2">
                                {/* Legend section - takes available space */}
                                <div className="flex items-center gap-4 flex-1">
                                    {experiment?.experiment_metadatas?.group_configs.map((group: any, i: any) => (
                                        <div className="flex items-center gap-2" key={`graph-legend-${group.name}-${i}`}>
                                            <div className="w-[0.583rem] h-[0.583rem] rounded-[0.167rem]" style={{ background: getChartColor(group.is_control ? 'control' : 'test', i) }}></div>
                                            <Text size="md">{group.name}</Text>
                                        </div>
                                    ))}
                                </div>

                                {/* Zoom controls - fixed width at the end */}
                                <div className="flex items-center shadow-1 rounded-md w-24 ml-auto">
                                    <button type="button" className="text-center w-1/2 cursor-pointer hover:bg-[#F5F5F5] rounded-e" onClick={zoomOut} title={t('Reset Zoom')}>
                                        <span className="font-normal text-nowrap">-</span>
                                    </button>
                                    <button type="button" className="border-[#D9D9D9] border-r text-center w-1/2 cursor-pointer hover:bg-[#F5F5F5] rounded-s" title={t('Zoom In')} onClick={selectZoneAndZoomIn}>
                                        <span className="font-normal text-nowrap">+</span>
                                    </button>
                                </div>
                            </div>

                            {/* <Button className="rounded-xl text-[.5rem] w-[4.5rem] mt-1" onClick={zoomOut}>
                                <span className="font-normal text-[.6rem] text-nowrap">Zoom Out</span>
                            </Button> */}
                        </div>
                    </div>
                    <div className="h-[18.542rem] text-[0.5rem] flex relative" style={{ width: `${chartWidth}px`, minWidth: '100%' }}>
                        <div className="w-[1rem] flex items-center justify-center">
                            <p className="text-[0.5rem] text-center leading-none font-semibold -rotate-90 whitespace-nowrap">
                                {t('Test / Control Group Allocation %')} of {title}
                            </p>
                        </div>

                        <ResponsiveContainer width="100%" height="100%" className="flex-grow">
                            <BarChart
                                data-testid="custom-bar"
                                width={chartWidth}
                                barGap={barGapWidth}
                                height={374}
                                data={zoomArea.data.slice(graphPagination * graphPageCount, graphPagination * graphPageCount + graphPageCount)}
                                margin={{
                                    top: 40,
                                    right: 0,
                                    left: 0,
                                    bottom: data.length > 12 ? 60 : 0,
                                }}
                                onMouseDown={handleMouseDown}
                                onMouseUp={handleMouseUp}
                                onMouseMove={onBarChartMouseMove}
                            >
                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                <XAxis dataKey="name" stroke="#939393" tickLine={false} interval={0} tick={<CustomTick />} />
                                <YAxis overlineThickness={1} domain={[0, 'dataMax']} tickLine={false} axisLine={false} ticks={ticks} label={<text></text>} width={25} />
                                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'transparent' }} />
                                {experiment?.experiment_metadatas?.group_configs.map((gr: any, i: number) => (
                                    <Bar
                                        data-testid="custom-bar"
                                        key={`gr-1-${gr.name}`}
                                        dataKey={`groups[${i}].value`}
                                        fill={getChartColor(gr.is_control ? 'control' : 'test', i)}
                                        radius={[7, 7, 7, 7]}
                                        barSize={barWidth}
                                        activeBar={<Rectangle fill={getChartColor(gr.is_control ? 'control' : 'test', i)} />}
                                    >
                                        <LabelList content={<CustomizedLabel />} />
                                    </Bar>
                                ))}
                                {zoomArea.refAreaLeft && zoomArea.refAreaRight ? <ReferenceArea x1={zoomArea.refAreaLeft} x2={zoomArea.refAreaRight} strokeOpacity={0.3} /> : null}
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                    <div className={`flex items-center ${zoomArea?.data?.length > 13 ? 'justify-between' : 'justify-center'}`}>
                        {zoomArea?.data?.length > 13 && (
                            <Button theme="tertiary" size="xxs" disabled={graphPagination == 0} onClick={paginationPrev}>
                                <Icon name="arrow-left" className="text-[0.75rem] text-[#000000A6]" />
                            </Button>
                        )}
                        <p className="text-[0.5rem] text-center leading-none font-semibold  whitespace-nowrap">{title}</p>
                        {zoomArea?.data?.length > 13 && (
                            <Button theme="tertiary" size="xxs" disabled={zoomArea?.data?.length <= graphPagination * graphPageCount + graphPageCount} onClick={paginationNext}>
                                <Icon name="arrow-right" className="text-[0.75rem] text-[#000000A6]" />
                            </Button>
                        )}
                    </div>
                </div>
            ) : null}
        </>
    )
}

export default GraphData
